import React, { useEffect, useRef, useState, useCallback } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { searchAddressMap } from "../../api/getMapAddress";
import { Input, Button, Spinner } from "reactstrap";

// Perbaikan bug icon default Leaflet di Webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
    iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
    shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

const defaultMarkerIcon = new L.Icon({
    iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
    iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
    shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41],
});

const Maps = ({
    defaultCenter,
    defaultZoom = 15,
    onClickMap,
    apiIsLoaded,
    height = "420px",
    width = "100%",
    long,
    lat,
    address,
    isSearch = true,
    setItem,
    setLong,
    setLat,
    isLoopMaker,
    absensi,
}) => {
    const mapContainerRef = useRef(null);
    const mapInstanceRef = useRef(null);
    const markerRef = useRef(null);
    const loopMarkersRef = useRef([]);

    const [searchQuery, setSearchQuery] = useState(address || "");
    const [searchResults, setSearchResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [showResults, setShowResults] = useState(false);
    const [isLocating, setIsLocating] = useState(false);

    // Dapatkan koordinat awal
    const getInitialCoords = () => {
        const parsedLat = parseFloat(lat);
        const parsedLng = parseFloat(long);
        if (!isNaN(parsedLat) && !isNaN(parsedLng) && parsedLat !== 0 && parsedLng !== 0) {
            return { lat: parsedLat, lng: parsedLng };
        }
        if (defaultCenter && defaultCenter.lat && defaultCenter.lng) {
            return { lat: parseFloat(defaultCenter.lat), lng: parseFloat(defaultCenter.lng) };
        }
        // Default Surabaya
        return { lat: -7.3106144, lng: 112.6841525 };
    };

    // Callback saat posisi berubah
    const handleLocationChange = useCallback((newLat, newLng) => {
        if (setLat) setLat(newLat);
        if (setLong) setLong(newLng);
        if (setItem) setItem({ lat: newLat, long: newLng });
        if (onClickMap) {
            onClickMap({ lat: newLat, lng: newLng });
        }
    }, [setLat, setLong, setItem, onClickMap]);

    const handleLocationChangeRef = useRef(handleLocationChange);
    useEffect(() => {
        handleLocationChangeRef.current = handleLocationChange;
    }, [handleLocationChange]);

    // Inisialisasi peta Leaflet
    useEffect(() => {
        if (!mapContainerRef.current) return;

        // Jika instance peta sudah ada, bersihkan terlebih dahulu
        if (mapInstanceRef.current) {
            mapInstanceRef.current.remove();
            mapInstanceRef.current = null;
        }

        const initial = getInitialCoords();

        const map = L.map(mapContainerRef.current, {
            center: [initial.lat, initial.lng],
            zoom: defaultZoom,
            scrollWheelZoom: true,
        });

        L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
            attribution: '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">OpenStreetMap</a> contributors',
            maxZoom: 19,
        }).addTo(map);

        mapInstanceRef.current = map;

        // Buat marker utama jika bukan loop marker absensi
        if (!isLoopMaker) {
            const marker = L.marker([initial.lat, initial.lng], {
                icon: defaultMarkerIcon,
                draggable: true,
            }).addTo(map);

            marker.bindPopup("<b>Titik Alamat Anda</b><br/>Geser marker untuk menyesuaikan.").openPopup();

            marker.on("dragend", () => {
                const pos = marker.getLatLng();
                marker.setPopupContent(`<b>Titik Terpilih</b><br/>Lat: ${pos.lat.toFixed(6)}, Lng: ${pos.lng.toFixed(6)}`).openPopup();
                if (handleLocationChangeRef.current) {
                    handleLocationChangeRef.current(pos.lat, pos.lng);
                }
            });

            markerRef.current = marker;
        }

        // Event klik pada peta
        map.on("click", (e) => {
            const { lat: clickedLat, lng: clickedLng } = e.latlng;
            if (markerRef.current) {
                markerRef.current.setLatLng([clickedLat, clickedLng]);
                markerRef.current.setPopupContent(`<b>Titik Terpilih</b><br/>Lat: ${clickedLat.toFixed(6)}, Lng: ${clickedLng.toFixed(6)}`).openPopup();
            }
            if (handleLocationChangeRef.current) {
                handleLocationChangeRef.current(clickedLat, clickedLng);
            }
        });

        // Panggil apiIsLoaded untuk kompatibilitas jika ada
        if (typeof apiIsLoaded === "function") {
            try {
                apiIsLoaded(map, L);
            } catch (err) {
                console.warn("apiIsLoaded call warning:", err);
            }
        }

        // Perbaiki rendering tile setelah mount
        const timer1 = setTimeout(() => {
            if (mapInstanceRef.current) {
                mapInstanceRef.current.invalidateSize();
            }
        }, 200);

        const timer2 = setTimeout(() => {
            if (mapInstanceRef.current) {
                mapInstanceRef.current.invalidateSize();
            }
        }, 500);

        return () => {
            clearTimeout(timer1);
            clearTimeout(timer2);
            if (mapInstanceRef.current) {
                mapInstanceRef.current.remove();
                mapInstanceRef.current = null;
            }
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Sinkronisasi marker & peta saat prop lat / long berubah dari luar (misal setelah getbyId)
    useEffect(() => {
        if (!mapInstanceRef.current || isLoopMaker) return;

        const parsedLat = parseFloat(lat);
        const parsedLng = parseFloat(long);

        if (isNaN(parsedLat) || isNaN(parsedLng) || parsedLat === 0 || parsedLng === 0) return;

        if (markerRef.current) {
            const currentPos = markerRef.current.getLatLng();
            const diffLat = Math.abs(currentPos.lat - parsedLat);
            const diffLng = Math.abs(currentPos.lng - parsedLng);

            // Hanya update jika berbeda signifikan untuk mencegah loop
            if (diffLat > 0.00005 || diffLng > 0.00005) {
                markerRef.current.setLatLng([parsedLat, parsedLng]);
                mapInstanceRef.current.setView([parsedLat, parsedLng], mapInstanceRef.current.getZoom());
            }
        } else {
            const marker = L.marker([parsedLat, parsedLng], {
                icon: defaultMarkerIcon,
                draggable: true,
            }).addTo(mapInstanceRef.current);

            marker.on("dragend", () => {
                const pos = marker.getLatLng();
                if (handleLocationChangeRef.current) {
                    handleLocationChangeRef.current(pos.lat, pos.lng);
                }
            });

            markerRef.current = marker;
            mapInstanceRef.current.setView([parsedLat, parsedLng], mapInstanceRef.current.getZoom());
        }
    }, [lat, long, isLoopMaker]);

    // Handle sinkronisasi input text alamat
    useEffect(() => {
        if (address) {
            setSearchQuery(address);
        }
    }, [address]);

    // Fitur Loop Maker jika digunakan untuk absensi / multi-point
    useEffect(() => {
        if (!isLoopMaker || !mapInstanceRef.current) return;

        // Bersihkan marker lama
        loopMarkersRef.current.forEach((m) => m.remove());
        loopMarkersRef.current = [];

        const bounds = [];

        if (absensi?.absensiCheckInPage?.length > 0) {
            absensi.absensiCheckInPage.forEach((v, i) => {
                if (v.latitude_dtg && v.longitude_dtg) {
                    const m = L.marker([parseFloat(v.latitude_dtg), parseFloat(v.longitude_dtg)], {
                        icon: defaultMarkerIcon,
                    }).addTo(mapInstanceRef.current);
                    m.bindPopup(`<b>Check In #${i + 1}</b>`);
                    loopMarkersRef.current.push(m);
                    bounds.push([parseFloat(v.latitude_dtg), parseFloat(v.longitude_dtg)]);
                }
            });
        }

        if (absensi?.absensiCheckOutPage?.length > 0) {
            absensi.absensiCheckOutPage.forEach((v, i) => {
                if (v.latitude_plg && v.longitude_plg) {
                    const m = L.marker([parseFloat(v.latitude_plg), parseFloat(v.longitude_plg)], {
                        icon: defaultMarkerIcon,
                    }).addTo(mapInstanceRef.current);
                    m.bindPopup(`<b>Check Out #${i + 1}</b>`);
                    loopMarkersRef.current.push(m);
                    bounds.push([parseFloat(v.latitude_plg), parseFloat(v.longitude_plg)]);
                }
            });
        }

        if (bounds.length > 0 && mapInstanceRef.current) {
            mapInstanceRef.current.fitBounds(bounds, { padding: [40, 40] });
        }
    }, [isLoopMaker, absensi]);

    // Cari lokasi via Nominatim (Leaflet Geocoding)
    const handleSearch = async (e) => {
        if (e) e.preventDefault();
        if (!searchQuery || !searchQuery.trim()) return;

        setIsSearching(true);
        setShowResults(true);
        try {
            const results = await searchAddressMap(searchQuery);
            setSearchResults(results);
        } catch (err) {
            console.error("Search error:", err);
            setSearchResults([]);
        } finally {
            setIsSearching(false);
        }
    };

    // Pilih salah satu hasil pencarian
    const handleSelectResult = (item) => {
        const selectedLat = parseFloat(item.lat);
        const selectedLng = parseFloat(item.lon);

        setSearchQuery(item.display_name);
        setShowResults(false);

        if (mapInstanceRef.current) {
            mapInstanceRef.current.setView([selectedLat, selectedLng], 16);
            if (markerRef.current) {
                markerRef.current.setLatLng([selectedLat, selectedLng]);
                markerRef.current.setPopupContent(`<b>${item.display_name}</b>`).openPopup();
            }
        }

        if (handleLocationChangeRef.current) {
            handleLocationChangeRef.current(selectedLat, selectedLng);
        }
    };

    // Deteksi lokasi saat ini menggunakan browser Geolocation
    const handleGetCurrentLocation = () => {
        if (!navigator.geolocation) {
            alert("Browser Anda tidak mendukung deteksi lokasi (Geolocation).");
            return;
        }

        setIsLocating(true);
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                setIsLocating(false);
                const currentLat = pos.coords.latitude;
                const currentLng = pos.coords.longitude;

                if (mapInstanceRef.current) {
                    mapInstanceRef.current.setView([currentLat, currentLng], 16);
                    if (markerRef.current) {
                        markerRef.current.setLatLng([currentLat, currentLng]);
                        markerRef.current.setPopupContent("<b>Lokasi Anda Saat Ini</b>").openPopup();
                    }
                }

                if (handleLocationChangeRef.current) {
                    handleLocationChangeRef.current(currentLat, currentLng);
                }
            },
            (err) => {
                setIsLocating(false);
                console.warn("Geolocation error:", err.message);
                alert("Gagal mendeteksi lokasi otomatis. Pastikan izin lokasi (GPS) telah diaktifkan.");
            },
            { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
        );
    };

    return (
        <div className="leaflet-map-wrapper mb-3" style={{ position: "relative" }}>
            {/* Search Box & GPS Button */}
            {isSearch && (
                <div className="mb-2">
                    <form onSubmit={handleSearch} className="d-flex align-items-center" style={{ gap: "8px" }}>
                        <div style={{ flex: 1, position: "relative" }}>
                            <Input
                                id="pac-input"
                                type="text"
                                className="form-control"
                                placeholder="Cari alamat, jalan, atau area..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                onFocus={() => {
                                    if (searchResults.length > 0) setShowResults(true);
                                }}
                                autoComplete="off"
                                style={{
                                    height: "42px",
                                    borderRadius: "10px",
                                    border: "1.5px solid #cbd5e1",
                                    fontSize: "0.88rem",
                                    fontFamily: "inherit",
                                    fontWeight: "500",
                                    color: "#1e293b",
                                    boxShadow: "none",
                                }}
                            />
                            {/* Dropdown hasil pencarian */}
                            {showResults && (
                                <div
                                    style={{
                                        position: "absolute",
                                        top: "100%",
                                        left: 0,
                                        right: 0,
                                        zIndex: 1050,
                                        backgroundColor: "#fff",
                                        borderRadius: "8px",
                                        boxShadow: "0 6px 16px rgba(0,0,0,0.14)",
                                        maxHeight: "260px",
                                        overflowY: "auto",
                                        marginTop: "4px",
                                        border: "1px solid #e2e8f0",
                                    }}
                                >
                                    {isSearching ? (
                                        <div className="p-3 text-center text-muted" style={{ fontSize: "13px" }}>
                                            <Spinner size="sm" color="primary" className="mr-2" />
                                            Mencari alamat via OpenStreetMap...
                                        </div>
                                    ) : searchResults.length > 0 ? (
                                        <ul className="list-unstyled mb-0">
                                            {searchResults.map((item, idx) => (
                                                <li
                                                    key={idx}
                                                    onClick={() => handleSelectResult(item)}
                                                    style={{
                                                        padding: "10px 14px",
                                                        borderBottom: idx < searchResults.length - 1 ? "1px solid #f1f3f5" : "none",
                                                        cursor: "pointer",
                                                        fontSize: "13px",
                                                        lineHeight: "1.4",
                                                        transition: "background 0.15s ease",
                                                    }}
                                                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f8f9fa")}
                                                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#fff")}
                                                >
                                                    <i className="fas fa-map-marker-alt text-danger mr-2" />
                                                    <span>{item.display_name}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    ) : (
                                        <div className="p-3 text-center text-muted" style={{ fontSize: "13px" }}>
                                            Tidak ada hasil untuk pencarian ini.
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                        <Button
                            type="submit"
                            color="primary"
                            disabled={isSearching}
                            style={{
                                minWidth: "85px",
                                height: "42px",
                                borderRadius: "10px",
                                fontSize: "0.88rem",
                                fontFamily: "inherit",
                                fontWeight: "600",
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                background: "#0064d2",
                                border: "none",
                            }}
                        >
                            {isSearching ? <Spinner size="sm" /> : <><i className="fas fa-search mr-1.5" /> Cari</>}
                        </Button>
                        <Button
                            type="button"
                            color="light"
                            className="border"
                            onClick={handleGetCurrentLocation}
                            disabled={isLocating}
                            title="Deteksi Lokasi Saya"
                            style={{
                                height: "42px",
                                borderRadius: "10px",
                                fontSize: "0.88rem",
                                fontFamily: "inherit",
                                fontWeight: "600",
                                whiteSpace: "nowrap",
                                display: "inline-flex",
                                alignItems: "center",
                                borderColor: "#cbd5e1",
                                background: "#ffffff",
                                color: "#1e293b",
                            }}
                        >
                            {isLocating ? (
                                <Spinner size="sm" color="primary" />
                            ) : (
                                <><i className="fas fa-crosshairs text-primary mr-1.5" /> Lokasi Saya</>
                            )}
                        </Button>
                    </form>
                </div>
            )}

            {/* Container Peta Leaflet */}
            <div
                style={{
                    position: "relative",
                    borderRadius: "8px",
                    overflow: "hidden",
                    border: "1px solid #e2e8f0",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                }}
            >
                <div
                    ref={mapContainerRef}
                    style={{
                        height: height,
                        width: width,
                        zIndex: 1,
                    }}
                />
            </div>

            {/* Petunjuk & Koordinat Info */}
            <div className="d-flex justify-content-between align-items-center mt-2" style={{ fontSize: "12px", color: "#64748b" }}>
                <span>
                    <i className="fas fa-info-circle text-primary mr-1" />
                    Klik peta atau geser pin marker untuk menentukan titik pengiriman.
                </span>
                {lat && long && (
                    <span className="badge badge-light border text-muted" style={{ fontWeight: 500 }}>
                        Lat: {parseFloat(lat).toFixed(5)}, Lng: {parseFloat(long).toFixed(5)}
                    </span>
                )}
            </div>
        </div>
    );
};

export default Maps;